import { Component, computed, input, OnDestroy, output, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { CSLReference } from '@sciflow/cite';
import {
  CslSchemaSelectorComponent,
  SfoUiJSONSchema7,
} from 'projects/ui-components/src/public-api';
import { debounceTime, Subject, takeUntil } from 'rxjs';
import { Reference } from '../../reference.model';
import { RenderingService } from '../../rendering.service';

import articleJournalSchema from '../../../../../../../dist/libs/cite/src/csl-schemas/article-journal.schema.json';
import articleMagazineSchema from '../../../../../../../dist/libs/cite/src/csl-schemas/article-magazine.schema.json';
import articleNewspaperSchema from '../../../../../../../dist/libs/cite/src/csl-schemas/article-newspaper.schema.json';
import articleSchema from '../../../../../../../dist/libs/cite/src/csl-schemas/article.schema.json';
import billSchema from '../../../../../../../dist/libs/cite/src/csl-schemas/bill.schema.json';
import bookSchema from '../../../../../../../dist/libs/cite/src/csl-schemas/book.schema.json';
import broadcastSchema from '../../../../../../../dist/libs/cite/src/csl-schemas/broadcast.schema.json';
import chapterSchema from '../../../../../../../dist/libs/cite/src/csl-schemas/chapter.schema.json';
import classicSchema from '../../../../../../../dist/libs/cite/src/csl-schemas/classic.schema.json';
import collectionSchema from '../../../../../../../dist/libs/cite/src/csl-schemas/collection.schema.json';
import datasetSchema from '../../../../../../../dist/libs/cite/src/csl-schemas/dataset.schema.json';
import documentSchema from '../../../../../../../dist/libs/cite/src/csl-schemas/document.schema.json';
import entryDictionarySchema from '../../../../../../../dist/libs/cite/src/csl-schemas/entry-dictionary.schema.json';
import entryEncyclopediaSchema from '../../../../../../../dist/libs/cite/src/csl-schemas/entry-encyclopedia.schema.json';
import entrySchema from '../../../../../../../dist/libs/cite/src/csl-schemas/entry.schema.json';
import eventSchema from '../../../../../../../dist/libs/cite/src/csl-schemas/event.schema.json';
import figureSchema from '../../../../../../../dist/libs/cite/src/csl-schemas/figure.schema.json';
import graphicSchema from '../../../../../../../dist/libs/cite/src/csl-schemas/graphic.schema.json';
import hearingSchema from '../../../../../../../dist/libs/cite/src/csl-schemas/hearing.schema.json';
import interviewSchema from '../../../../../../../dist/libs/cite/src/csl-schemas/interview.schema.json';
import legalCaseSchema from '../../../../../../../dist/libs/cite/src/csl-schemas/legal_case.schema.json';
import legislationSchema from '../../../../../../../dist/libs/cite/src/csl-schemas/legislation.schema.json';
import manuscriptSchema from '../../../../../../../dist/libs/cite/src/csl-schemas/manuscript.schema.json';
import mapSchema from '../../../../../../../dist/libs/cite/src/csl-schemas/map.schema.json';
import motionPictureSchema from '../../../../../../../dist/libs/cite/src/csl-schemas/motion_picture.schema.json';
import musicalScoreSchema from '../../../../../../../dist/libs/cite/src/csl-schemas/musical_score.schema.json';
import pamphletSchema from '../../../../../../../dist/libs/cite/src/csl-schemas/pamphlet.schema.json';
import paperConferenceSchema from '../../../../../../../dist/libs/cite/src/csl-schemas/paper-conference.schema.json';
import patentSchema from '../../../../../../../dist/libs/cite/src/csl-schemas/patent.schema.json';
import performanceSchema from '../../../../../../../dist/libs/cite/src/csl-schemas/performance.schema.json';
import periodicalSchema from '../../../../../../../dist/libs/cite/src/csl-schemas/periodical.schema.json';
import personalCommunicationSchema from '../../../../../../../dist/libs/cite/src/csl-schemas/personal_communication.schema.json';
import postWeblogSchema from '../../../../../../../dist/libs/cite/src/csl-schemas/post-weblog.schema.json';
import postSchema from '../../../../../../../dist/libs/cite/src/csl-schemas/post.schema.json';
import regulationSchema from '../../../../../../../dist/libs/cite/src/csl-schemas/regulation.schema.json';
import reportSchema from '../../../../../../../dist/libs/cite/src/csl-schemas/report.schema.json';
import reviewBookSchema from '../../../../../../../dist/libs/cite/src/csl-schemas/review-book.schema.json';
import reviewSchema from '../../../../../../../dist/libs/cite/src/csl-schemas/review.schema.json';
import softwareSchema from '../../../../../../../dist/libs/cite/src/csl-schemas/software.schema.json';
import songSchema from '../../../../../../../dist/libs/cite/src/csl-schemas/song.schema.json';
import speechSchema from '../../../../../../../dist/libs/cite/src/csl-schemas/speech.schema.json';
import standardSchema from '../../../../../../../dist/libs/cite/src/csl-schemas/standard.schema.json';
import thesisSchema from '../../../../../../../dist/libs/cite/src/csl-schemas/thesis.schema.json';
import treatySchema from '../../../../../../../dist/libs/cite/src/csl-schemas/treaty.schema.json';
import webpageSchema from '../../../../../../../dist/libs/cite/src/csl-schemas/webpage.schema.json';

const preResolvedSchemas = [
  articleJournalSchema,
  articleMagazineSchema,
  articleNewspaperSchema,
  articleSchema,
  billSchema,
  bookSchema,
  broadcastSchema,
  chapterSchema,
  classicSchema,
  collectionSchema,
  datasetSchema,
  documentSchema,
  entryDictionarySchema,
  entryEncyclopediaSchema,
  entrySchema,
  eventSchema,
  figureSchema,
  graphicSchema,
  hearingSchema,
  interviewSchema,
  legalCaseSchema,
  legislationSchema,
  manuscriptSchema,
  mapSchema,
  motionPictureSchema,
  musicalScoreSchema,
  pamphletSchema,
  paperConferenceSchema,
  patentSchema,
  performanceSchema,
  periodicalSchema,
  personalCommunicationSchema,
  postSchema,
  postWeblogSchema,
  regulationSchema,
  reportSchema,
  reviewBookSchema,
  reviewSchema,
  softwareSchema,
  songSchema,
  speechSchema,
  standardSchema,
  thesisSchema,
  treatySchema,
  webpageSchema,
] as SfoUiJSONSchema7[];

@Component({
  selector: 'sfo-reference-editor',
  templateUrl: './reference-editor.component.html',
  styleUrls: ['./reference-editor.component.scss'],
  standalone: true,
  imports: [MatButtonModule, MatIconModule, CslSchemaSelectorComponent],
})
export class ReferenceEditorComponent implements OnDestroy {
  private formValue = signal<object | null>(null);
  private delayFormValueEmission$ = new Subject<object>();
  private destroy$ = new Subject<void>();

  readonly reference = input.required<Reference>();
  readonly cslSchemas = signal<SfoUiJSONSchema7[]>(preResolvedSchemas);
  updateReference = output<Reference>();

  constructor(private renderingService: RenderingService) {
    this.delayFormValueEmission$
      .pipe(debounceTime(500), takeUntil(this.destroy$))
      .subscribe((value) => {
        this.emitReferenceUpdate(value);
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  cslData = computed(() => {
    return this.reference()?.['csl'] || null;
  });

  onFormValueChange(value: object) {
    this.formValue.set(value);
    this.delayFormValueEmission$.next(value);
  }

  private emitReferenceUpdate(value: object) {
    const updatedReference = this.rebuildReferenceBib({
      ...this.reference(),
      csl: {
        ...this.reference()?.['csl'],
        ...value,
      } as CSLReference,
    });

    this.updateReference.emit(updatedReference);
  }

  private rebuildReferenceBib(reference: Reference): Reference {
    if (!reference?.csl) {
      return reference;
    }

    try {
      const bibliography = this.renderingService.getBibliography([reference.csl]);
      const bib = bibliography?.[0];
      if (!bib) {
        return reference;
      }

      return {
        ...reference,
        bib: bib as unknown as Reference['bib'],
      };
    } catch {
      return reference;
    }
  }
}
